import Colophon from './Colophon';
import LogoTitle from './LogoTitle';
import React, {useCallback, useMemo, useState} from 'react';
import diorama from '../assets/images/diorama.jpg';
import mobile from 'is-mobile';
import querystring from 'querystring';
import {
  Box,
  Button,
  Flex,
  Heading,
  Img,
  Link,
  Stack,
  Text,
  useBreakpointValue
} from '@chakra-ui/react';
import {CONTAINER_GAP, CONTAINER_PADDING_X} from '../utils';
import {Link as GatsbyLink, graphql, useStaticQuery} from 'gatsby';
import {useApolloClient} from '@apollo/client';
import {useGoogleLogin} from 'react-google-login';
import {useMount} from 'react-use';

export default function LoginPage() {
  const client = useApolloClient();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const buttonSize = useBreakpointValue({md: 'lg'});
  const {config} = useStaticQuery(
    graphql`
      {
        config {
          googleClientId
        }
      }
    `
  );

  const setToken = useCallback(
    token => {
      localStorage.setItem('playback:token', token);
      client.resetStore();
    },
    [client]
  );

  const handleSuccess = useCallback(
    async ({tokenId}) => {
      setLoading(true);

      const {i} = querystring.parse(location.search.slice(1));
      const response = await fetch(`${process.env.GATSBY_API_URL}/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: tokenId,
          inviteToken: i
        })
      });

      if (response.ok) {
        const {token, isNew} = await response.json();
        setToken(token);
        if (isNew) {
          // track new signups
          window.fbq?.('track', 'Lead');
        }
      } else {
        const errorMessage = await response.text();
        setError(new Error(errorMessage));
        setLoading(false);
      }
    },
    [setToken]
  );

  useMount(() => {
    // hacky fix for mobile (specifically iOS safari webview)
    // we need to use the redirect ux mode for this to work, and so this page
    // needs to function as a redirect URL
    // we try to parse the hash that google puts in the URL
    const [query] = decodeURIComponent(location.hash.slice(1))
      .split(' ')
      // the querystring is the last element in the space-delimited hash
      .reverse();
    if (query) {
      // try to parse a token from the querystring
      const {id_token} = querystring.parse(query);
      if (id_token) {
        handleSuccess({tokenId: id_token});
      }
    }
  });

  const loginParams = useMemo(() => {
    const params = {
      clientId: config.googleClientId,
      onSuccess: handleSuccess,
      onFailure: error => console.log(error)
    };
    return mobile()
      ? {
          ...params,
          uxMode: 'redirect',
          redirectUri: location.origin + '/projects'
        }
      : params;
  }, [config.googleClientId, handleSuccess]);

  const {signIn, loaded} = useGoogleLogin(loginParams);

  return (
    <Box h="100vh" bgImage={`url(${diorama})`} bgSize="cover" bgPos="center">
      <Flex
        h="full"
        align="center"
        p={CONTAINER_PADDING_X}
        maxW="container.xl"
        mx="auto"
      >
        <Stack spacing={CONTAINER_GAP} w={{md: '50%'}}>
          <LogoTitle />
          <Box bg="white" rounded="xl" p={CONTAINER_GAP}>
            <Heading size="xl" mb={{base: 4, md: 6}} fontWeight="semibold">
              A better filmmaking experience awaits.
            </Heading>
            {error && (
              <Text mb="4" color="red.500">
                {error.message}
              </Text>
            )}
            <Button
              size={buttonSize}
              isLoading={loading}
              isDisabled={!loaded}
              leftIcon={
                <Img
                  flexShrink="0"
                  src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg"
                  boxSize={{base: 5, md: 6}}
                />
              }
              onClick={signIn}
            >
              Continue with Google
            </Button>
            {/* <SidebarDivider my="6" as={FiMoon} /> */}
            {/* <LoginForm setToken={setToken} /> */}
          </Box>
          <Box fontSize="sm">
            <Colophon as="span" /> &bull;{' '}
            <Link as={GatsbyLink} to="/">
              Back to home
            </Link>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
}
